import type FamilyPortalAPIClient from '../../auth/FamilyPortalAPIClient'
import type { EhiDownloadCounts, EhiExportStatus, EhiExportTask } from '../types'

const BASE_PATH = '/fasten/ehi-export'
/** WebKit may read blob URLs asynchronously after programmatic click; revoking too soon breaks saves. */
const BLOB_DOWNLOAD_REVOKE_DELAY_MS = 60_000

/** Retries when GET status returns 503 (transient Fasten/network errors). Initial attempt + this many retries. */
export const STATUS_CHECK_503_MAX_RETRIES = 3
export const STATUS_CHECK_503_RETRY_DELAY_MS = 5000

async function readDetail(res: Response, fallback: string): Promise<string> {
  return res
    .json()
    .then((body: { detail?: unknown }) => (typeof body?.detail === 'string' ? body.detail : fallback))
    .catch(() => fallback)
}

type StatusCheckOptions = {
  max503Retries?: number
  sleep?: (ms: number) => Promise<void>
}

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

export async function triggerBulkExport(
  client: FamilyPortalAPIClient,
  orgConnectionId: string
): Promise<EhiExportTask> {
  const res = await client.post(BASE_PATH, { orgConnectionId })
  if (!res.ok) {
    throw new Error(await readDetail(res, `Failed to trigger export (status ${res.status})`))
  }
  return (await res.json()) as EhiExportTask
}

export async function checkExportStatus(
  client: FamilyPortalAPIClient,
  orgConnectionId: string,
  options: StatusCheckOptions = {}
): Promise<EhiExportStatus> {
  const max503Retries = options.max503Retries ?? STATUS_CHECK_503_MAX_RETRIES
  const sleep = options.sleep ?? defaultSleep
  const path = `${BASE_PATH}/${encodeURIComponent(orgConnectionId)}`

  for (let attempt = 0; attempt <= max503Retries; attempt += 1) {
    if (attempt > 0) {
      await sleep(STATUS_CHECK_503_RETRY_DELAY_MS)
    }
    const res = await client.get(path)
    if (res.ok) {
      return (await res.json()) as EhiExportStatus
    }
    if (res.status === 503 && attempt < max503Retries) {
      continue
    }
    throw new Error(await readDetail(res, `Failed to check export status (status ${res.status})`))
  }

  throw new Error('Failed to check export status (status 503)')
}

export async function downloadExport(
  client: FamilyPortalAPIClient,
  orgConnectionId: string
): Promise<EhiDownloadCounts> {
  const res = await client.get(`${BASE_PATH}/${encodeURIComponent(orgConnectionId)}/download`)
  if (!res.ok) {
    throw new Error(await readDetail(res, `Failed to download export (status ${res.status})`))
  }
  return (await res.json()) as EhiDownloadCounts
}

function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  const match = /filename="([^"]+)"/i.exec(header)
  return match?.[1] ?? null
}

export async function downloadExportArchive(client: FamilyPortalAPIClient, orgConnectionId: string): Promise<void> {
  const res = await client.get(`${BASE_PATH}/${encodeURIComponent(orgConnectionId)}/download/archive`, {
    Accept: 'application/zip',
  })
  if (!res.ok) {
    throw new Error(await readDetail(res, `Failed to download export archive (status ${res.status})`))
  }
  const blob = await res.blob()
  const filename =
    filenameFromContentDisposition(res.headers.get('Content-Disposition')) ?? `ehi-export-${orgConnectionId}.zip`
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.rel = 'noopener'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.setTimeout(() => URL.revokeObjectURL(url), BLOB_DOWNLOAD_REVOKE_DELAY_MS)
}
